import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';

const Icon = ({ d, ...p }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d={d} />
  </svg>
);

const TYPE_CONFIG = {
  contact:     { icon:'👤', label:'Contact',     color:'#E6F1FB', text:'#0C447C' },
  account:     { icon:'🏢', label:'Account',     color:'#EAF3DE', text:'#27500A' },
  opportunity: { icon:'💼', label:'Opportunity', color:'#FAEEDA', text:'#633806' },
  event:       { icon:'📅', label:'Event',       color:'#FBEAF0', text:'#72243E' },
};

function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef();
  const inputRef = useRef();

  useEffect(() => {
    const handleClick = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(() => {
      setLoading(true);
      api.get(`/search?q=${encodeURIComponent(query)}`)
        .then(r => { setResults(r.data.results || []); setOpen(true); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const handleSelect = (result) => {
    setQuery('');
    setOpen(false);
    setResults([]);
    if (result.type === 'contact') navigate(`/contacts/${result.id}`);
    else if (result.type === 'account') navigate(`/accounts/${result.id}`);
    else if (result.type === 'opportunity') navigate(`/pipeline/${result.id}`);
    else if (result.type === 'event') navigate('/calendar');
  };

  const fmt = n => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0 });

  return (
    <div ref={ref} style={{ position:'relative', flex:1, maxWidth:340 }}>
      <div className="search-wrap">
        <Icon d="M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0" />
        <input
          ref={inputRef}
          className="search-input"
          placeholder="Search contacts, accounts, opportunities…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          style={{ width:'100%' }}
        />
        {loading && (
          <div style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                        fontSize:11, color:'var(--text-tertiary)' }}>…</div>
        )}
      </div>

      {/* Results dropdown */}
      {open && results.length > 0 && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0,
          background:'var(--bg)', border:'0.5px solid var(--border-md)',
          borderRadius:'var(--radius-lg)', boxShadow:'0 8px 24px rgba(0,0,0,0.12)',
          zIndex:1000, overflow:'hidden', maxHeight:400, overflowY:'auto'
        }}>
          {/* Group by type */}
          {['contact','account','opportunity','event'].map(type => {
            const typeResults = results.filter(r => r.type === type);
            if (!typeResults.length) return null;
            const cfg = TYPE_CONFIG[type];
            return (
              <div key={type}>
                <div style={{ padding:'6px 12px 4px', fontSize:10, fontWeight:600,
                               color:'var(--text-tertiary)', textTransform:'uppercase',
                               letterSpacing:'.05em', background:'var(--bg-secondary)',
                               borderBottom:'0.5px solid var(--border)' }}>
                  {cfg.label}s
                </div>
                {typeResults.map(r => (
                  <div key={r.id}
                    onClick={() => handleSelect(r)}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
                             cursor:'pointer', borderBottom:'0.5px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg)'}
                  >
                    <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
                                  background: cfg.color, display:'flex', alignItems:'center',
                                  justifyContent:'center', fontSize:14 }}>
                      {cfg.icon}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500, overflow:'hidden',
                                     textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {r.name}
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:1,
                                     overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {r.subtitle || r.email || r.title || ''}
                        {r.type === 'opportunity' && r.amount ? ` · ${fmt(r.amount)}` : ''}
                        {r.type === 'opportunity' && r.stage ? ` · ${r.stage.replace('_',' ')}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize:10, padding:'2px 6px', borderRadius:20, flexShrink:0,
                                   background: cfg.color, color: cfg.text, fontWeight:500 }}>
                      {cfg.label}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}

          {results.length === 0 && !loading && (
            <div style={{ padding:'16px 12px', fontSize:13, color:'var(--text-tertiary)', textAlign:'center' }}>
              No results for "{query}"
            </div>
          )}
        </div>
      )}

      {/* No results state */}
      {open && query.length >= 2 && results.length === 0 && !loading && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0,
          background:'var(--bg)', border:'0.5px solid var(--border-md)',
          borderRadius:'var(--radius-lg)', padding:'14px 12px',
          fontSize:13, color:'var(--text-tertiary)', textAlign:'center',
          boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:1000
        }}>
          No results for "{query}"
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();

  const location = useLocation();
const path = '/' + location.pathname.split('/')[1];
  const titles = {
    '/dashboard': 'Dashboard', '/contacts': 'Contacts', '/accounts': 'Accounts',
    '/pipeline': 'Opportunities', '/activity': 'Activity', '/calendar': 'Calendar',
    '/reports': 'Reports', '/outlook': 'Outlook add-in', '/users': 'Users',
    '/settings': 'Settings', '/import': 'Import accounts',
  };
  const title = titles[path] || '';

  return (
    <div className="app-layout">
      <nav className="sidebar">
        <div className="sidebar-logo">
  <img src="/logo.png" alt="ApexRep" style={{ height:36, width:'auto', maxWidth:'100%' }} />
</div>

        <NavLink to="/dashboard" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10" /> Dashboard
        </NavLink>
        <NavLink to="/contacts" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" /> Contacts
        </NavLink>
        <NavLink to="/accounts" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8" /> Accounts
        </NavLink>
        <NavLink to="/pipeline" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" /> Opportunities
        </NavLink>
        <NavLink to="/activity" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2" /> Activity
        </NavLink>
        <NavLink to="/calendar" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M3 4h18v18H3z M16 2v4 M8 2v4 M3 10h18" /> Calendar
        </NavLink>
        <NavLink to="/reports" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M18 20V10 M12 20V4 M6 20v-6" /> Reports
        </NavLink>

        <div className="nav-section">Integrations</div>
        <NavLink to="/outlook" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6" /> Outlook
        </NavLink>

        <div className="nav-section">Admin</div>
        <NavLink to="/users" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" /> Users
        </NavLink>
        <NavLink to="/import" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12" /> Import
        </NavLink>
        <NavLink to="/settings" className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
          <Icon d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /> Settings
        </NavLink>

        <div style={{ flex: 1 }} />
        <div style={{ padding: '12px 16px', borderTop: '0.5px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="avatar" style={{ width: 28, height: 28, fontSize: 10 }}>
              {user?.avatar_initials || '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.full_name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{user?.role}</div>
            </div>
            <button onClick={logout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0 }} title="Log out">
              <Icon d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9" style={{ width: 15 }} />
            </button>
          </div>
        </div>
      </nav>

      <div className="main-area">
        <header className="topbar">
          <div className="topbar-title">{title}</div>
          <GlobalSearch />
          <button className="btn btn-primary">
            <Icon d="M12 5v14 M5 12h14" /> New
          </button>
        </header>
        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}